import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import {
  Observable,
  catchError,
  delay,
  finalize,
  mergeMap,
  of,
  retryWhen,
  shareReplay,
  switchMap,
  throwError,
} from 'rxjs';
import { ToasterService } from '../service/toaster.service';
import { LoaderService } from '../service/loader.service';
import { AuthService } from '../service/auth.service';

export const maxRetries = 2;
export const delayMs = 2000;

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private refreshInProgress$: Observable<any> | null = null;

  constructor(
    private toastr: ToasterService,
    public loader: LoaderService,
    private authService: AuthService
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      retryWhen((errors) =>
        errors.pipe(
          mergeMap((error, index) => {
            if (index < maxRetries && error?.status === 500) {
              this.loader.setLoading(false);
              return of(error).pipe(delay(delayMs));
            }
            return throwError(() => error);
          })
        )
      ),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && this.shouldAttemptRefresh(request)) {
          return this.handleUnauthorized(request, next);
        }

        if (error.status === 401) {
          this.loader.setLoading(false);
          this.authService.forceLogout();
          return throwError(() => error);
        }

        const message = error?.error?.message || 'Something went wrong';
        this.toastr.error(message, 'Error');
        this.loader.setLoading(false);
        return throwError(() => error);
      })
    );
  }

  private shouldAttemptRefresh(request: HttpRequest<unknown>): boolean {
    const url = request.url || '';
    if (url.includes('/v2/refresh-token')) {
      return false;
    }
    if (
      url.includes('/verify-contractor-otp') ||
      url.includes('/v2/send-contractor-otp') ||
      url.includes('/logout')
    ) {
      return false;
    }
    return !!this.authService.getAuthDataSync()?.refresh_token;
  }

  private handleUnauthorized(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (!this.refreshInProgress$) {
      this.refreshInProgress$ = this.authService.refreshToken().pipe(
        shareReplay(1),
        finalize(() => {
          this.refreshInProgress$ = null;
        })
      );
    }

    return this.refreshInProgress$.pipe(
      switchMap(() => {
        const authData = this.authService.getAuthDataSync();
        const token = authData?.token;
        const retryRequest = token
          ? request.clone({
              setHeaders: {
                Authorization: `Bearer ${token}`,
              },
            })
          : request;
        return next.handle(retryRequest);
      }),
      catchError((refreshError) => {
        this.loader.setLoading(false);
        this.authService.forceLogout();
        return throwError(() => refreshError);
      })
    );
  }
}
